/**
 * Local persistence for the alphabet drill.
 *
 * The same shape as `persist.ts`: a single versioned localStorage key, a version check on
 * read, and writes that return a boolean rather than throwing — a drill on a locked-down
 * browser where storage is unavailable should still work for the session, just not remember
 * across reloads. Nothing here is provenance and nothing is sent to the server, so there is no
 * ownership dimension: the drill is a private study aid, and its state stays in the browser.
 */

import type { LetterStat } from '../alphabet';

const KEY = 'hismaic.alphabet.v1';
const VERSION = 1;

/** Per-letter stats keyed by vocabulary class id. */
export type ProgressMap = Record<number, LetterStat>;

interface StoredProgress {
  version: number;
  stats: ProgressMap;
}

function isLetterStat(value: unknown): value is LetterStat {
  if (typeof value !== 'object' || value === null) return false;
  const stat = value as Record<string, unknown>;
  return (
    typeof stat['bucket'] === 'number' &&
    typeof stat['right'] === 'number' &&
    typeof stat['wrong'] === 'number' &&
    typeof stat['lastSeen'] === 'string'
  );
}

/** Read the stored progress, or an empty map when there is none, it is corrupt, or the version moved. */
export function loadProgress(): ProgressMap {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as StoredProgress;
    if (parsed.version !== VERSION || typeof parsed.stats !== 'object' || parsed.stats === null) {
      return {};
    }
    const clean: ProgressMap = {};
    for (const [id, stat] of Object.entries(parsed.stats)) {
      const numericId = Number(id);
      if (Number.isInteger(numericId) && isLetterStat(stat)) clean[numericId] = stat;
    }
    return clean;
  } catch {
    return {};
  }
}

/** Persist the whole progress map. Returns whether it reached storage. */
export function saveProgress(stats: ProgressMap): boolean {
  if (typeof localStorage === 'undefined') return false;
  const payload: StoredProgress = { version: VERSION, stats };
  try {
    localStorage.setItem(KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/** Forget everything — the "reset progress" button. */
export function clearProgress(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing useful to do */
  }
}
